<template>
  <div class="glass-datepicker" :class="{ 'has-error': hasError, 'is-disabled': disabled }">
    <label v-if="label" class="datepicker-label">
      {{ label }}
      <span v-if="required" class="required">*</span>
    </label>
    
    <div class="datepicker-wrapper" ref="wrapper">
      <div class="input-container" ref="inputContainer" @click="toggleCalendar">
        <i class="fas fa-calendar-alt input-icon"></i>
        <input
          type="text"
          :value="formattedValue"
          :placeholder="placeholder"
          :disabled="disabled"
          readonly
          class="datepicker-input"
        />
        <button 
          v-if="modelValue && !disabled" 
          type="button" 
          class="clear-btn"
          @click.stop="clearValue"
        >
          <i class="fas fa-times"></i>
        </button>
      </div>
      
      <Teleport to="body">
        <transition name="dropdown">
          <div v-if="isOpen" class="calendar-dropdown" :style="dropdownStyle" ref="calendarDropdown" @click.stop>
          <!-- Calendar Header -->
          <div class="calendar-header">
            <button type="button" class="nav-btn" @click="prevMonth">
              <i class="fas fa-chevron-left"></i>
            </button>
            <div class="header-selects">
              <select v-model="viewMonth" class="month-select">
                <option v-for="(month, index) in months" :key="index" :value="index">
                  {{ month }}
                </option>
              </select>
              <select v-model="viewYear" class="year-select">
                <option v-for="year in years" :key="year" :value="year">
                  {{ year }}
                </option>
              </select>
            </div>
            <button type="button" class="nav-btn" @click="nextMonth">
              <i class="fas fa-chevron-right"></i>
            </button>
          </div>
          
          <!-- Calendar Grid -->
          <div class="calendar-grid">
            <div class="weekday-header">
              <span v-for="day in weekdays" :key="day" class="weekday">{{ day }}</span>
            </div>
            <div class="days-grid">
              <button
                v-for="day in calendarDays"
                :key="day.key"
                type="button"
                class="day-btn"
                :class="{
                  'is-other-month': day.isOtherMonth,
                  'is-today': day.isToday,
                  'is-selected': day.isSelected,
                  'is-disabled': day.isDisabled
                }"
                :disabled="day.isDisabled"
                @click="selectDate(day.date)"
              >
                {{ day.dayNumber }}
              </button>
            </div>
          </div>
          
          <!-- Calendar Footer -->
          <div class="calendar-footer">
            <button type="button" class="today-btn" @click="goToToday">
              Aujourd'hui
            </button>
          </div>
        </div>
      </transition>
      </Teleport>
    </div>
    
    <span v-if="hasError && errorMessage" class="error-message">
      {{ errorMessage }}
    </span>
  </div>
</template>

<script>
export default {
  name: 'GlassDatepicker',
  emits: ['update:modelValue', 'change'],
  props: {
    modelValue: {
      type: [String, Date],
      default: ''
    },
    label: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: 'Sélectionner une date'
    },
    disabled: {
      type: Boolean,
      default: false
    },
    required: {
      type: Boolean,
      default: false
    },
    hasError: {
      type: Boolean,
      default: false
    },
    errorMessage: {
      type: String,
      default: ''
    },
    min: {
      type: [String, Date],
      default: ''
    },
    max: {
      type: [String, Date],
      default: ''
    },
    locale: {
      type: String,
      default: 'fr-FR'
    }
  },
  data() {
    const today = new Date();
    return {
      isOpen: false,
      viewMonth: today.getMonth(),
      viewYear: today.getFullYear(),
      dropdownStyle: {},
      weekdays: ['Lu', 'Ma', 'Me', 'Je', 'Ve', 'Sa', 'Di'],
      months: [
        'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
        'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
      ]
    };
  },
  computed: {
    formattedValue() {
      if (!this.modelValue) return '';
      const date = new Date(this.modelValue);
      return date.toLocaleDateString(this.locale, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    },
    years() {
      const currentYear = new Date().getFullYear();
      const years = [];
      for (let i = currentYear - 100; i <= currentYear + 10; i++) {
        years.push(i);
      }
      return years;
    },
    calendarDays() {
      const days = [];
      const firstDay = new Date(this.viewYear, this.viewMonth, 1);
      const lastDay = new Date(this.viewYear, this.viewMonth + 1, 0);
      
      // Get Monday as first day of week (ISO)
      let startDay = firstDay.getDay();
      startDay = startDay === 0 ? 6 : startDay - 1;
      
      // Previous month days
      const prevMonth = new Date(this.viewYear, this.viewMonth, 0);
      for (let i = startDay - 1; i >= 0; i--) {
        const date = new Date(this.viewYear, this.viewMonth - 1, prevMonth.getDate() - i);
        days.push(this.createDay(date, true));
      }
      
      // Current month days
      for (let i = 1; i <= lastDay.getDate(); i++) {
        const date = new Date(this.viewYear, this.viewMonth, i);
        days.push(this.createDay(date, false));
      }
      
      // Next month days to fill grid
      const remaining = 42 - days.length;
      for (let i = 1; i <= remaining; i++) {
        const date = new Date(this.viewYear, this.viewMonth + 1, i);
        days.push(this.createDay(date, true));
      }
      
      return days;
    }
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside);
    window.addEventListener('scroll', this.updatePosition, true);
    window.addEventListener('resize', this.updatePosition);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
    window.removeEventListener('scroll', this.updatePosition, true);
    window.removeEventListener('resize', this.updatePosition);
  },
  methods: {
    createDay(date, isOtherMonth) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const compareDate = new Date(date);
      compareDate.setHours(0, 0, 0, 0);
      
      const selectedDate = this.modelValue ? new Date(this.modelValue) : null;
      if (selectedDate) selectedDate.setHours(0, 0, 0, 0);
      
      let isDisabled = false;
      if (this.min) {
        const minDate = new Date(this.min);
        minDate.setHours(0, 0, 0, 0);
        if (compareDate < minDate) isDisabled = true;
      }
      if (this.max) {
        const maxDate = new Date(this.max);
        maxDate.setHours(0, 0, 0, 0);
        if (compareDate > maxDate) isDisabled = true;
      }
      
      return {
        key: date.toISOString(),
        date: date,
        dayNumber: date.getDate(),
        isOtherMonth,
        isToday: compareDate.getTime() === today.getTime(),
        isSelected: selectedDate && compareDate.getTime() === selectedDate.getTime(),
        isDisabled
      };
    },
    toggleCalendar() {
      if (!this.disabled) {
        this.isOpen = !this.isOpen;
        if (this.isOpen) {
          if (this.modelValue) {
            const date = new Date(this.modelValue);
            this.viewMonth = date.getMonth();
            this.viewYear = date.getFullYear();
          }
          this.$nextTick(() => this.updatePosition());
        }
      }
    },
    updatePosition() {
      if (!this.isOpen || !this.$refs.inputContainer) return;
      const rect = this.$refs.inputContainer.getBoundingClientRect();
      const dropdownHeight = 360;
      const spaceBelow = window.innerHeight - rect.bottom;
      const top = spaceBelow >= dropdownHeight
        ? rect.bottom + 4
        : rect.top - dropdownHeight - 4;
      this.dropdownStyle = {
        position: 'fixed',
        top: `${top}px`,
        left: `${rect.left}px`,
        minWidth: `${Math.max(rect.width, 280)}px`,
        zIndex: 99999,
      };
    },
    selectDate(date) {
      const formatted = this.formatDateValue(date);
      this.$emit('update:modelValue', formatted);
      this.$emit('change', formatted);
      this.isOpen = false;
    },
    formatDateValue(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },
    prevMonth() {
      if (this.viewMonth === 0) {
        this.viewMonth = 11;
        this.viewYear--;
      } else {
        this.viewMonth--;
      }
    },
    nextMonth() {
      if (this.viewMonth === 11) {
        this.viewMonth = 0;
        this.viewYear++;
      } else {
        this.viewMonth++;
      }
    },
    goToToday() {
      const today = new Date();
      this.viewMonth = today.getMonth();
      this.viewYear = today.getFullYear();
      this.selectDate(today);
    },
    clearValue() {
      this.$emit('update:modelValue', '');
      this.$emit('change', '');
    },
    handleClickOutside(e) {
      const dropdown = this.$refs.calendarDropdown;
      if (this.$refs.wrapper && !this.$refs.wrapper.contains(e.target) && (!dropdown || !dropdown.contains(e.target))) {
        this.isOpen = false;
      }
    }
  }
};
</script>

<style scoped>
.glass-datepicker {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.datepicker-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--color-text);
}

.required {
  color: var(--color-danger);
  margin-left: 0.25rem;
}

.datepicker-wrapper {
  position: relative;
}

.input-container {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0 1rem;
  height: 3rem;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: var(--border-radius);
  cursor: pointer;
  transition: all 0.2s ease;
}

.input-container:hover:not(.is-disabled) {
  border-color: var(--color-primary);
}

.input-icon {
  color: var(--color-text-muted);
}

.datepicker-input {
  flex: 1;
  background: transparent;
  border: none;
  color: var(--color-text);
  font-size: 0.9375rem;
  cursor: pointer;
  outline: none;
}

.datepicker-input::placeholder {
  color: var(--color-text-muted);
}

.clear-btn {
  padding: 0.25rem;
  background: transparent;
  border: none;
  color: var(--color-text-muted);
  cursor: pointer;
  border-radius: 50%;
  transition: all 0.2s ease;
}

.clear-btn:hover {
  color: var(--color-danger);
  background: rgba(255, 255, 255, 0.1);
}

.has-error .input-container {
  border-color: var(--color-danger);
}

.error-message {
  font-size: 0.8125rem;
  color: var(--color-danger);
}

.is-disabled .input-container {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>

<style>
/* Unscoped styles for teleported calendar dropdown */
.calendar-dropdown {
  padding: 1rem;
  background: rgba(30, 40, 55, 0.98);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(20px);
}

.calendar-dropdown .calendar-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
}

.calendar-dropdown .nav-btn {
  width: 2rem;
  height: 2rem;
  padding: 0;
  background: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: 50%;
  color: #e0e0e0;
  cursor: pointer;
  transition: all 0.2s ease;
}

.calendar-dropdown .nav-btn:hover {
  background: var(--color-primary, #3b88a5);
  color: white;
}

.calendar-dropdown .header-selects {
  display: flex;
  gap: 0.5rem;
}

.calendar-dropdown .month-select,
.calendar-dropdown .year-select {
  padding: 0.25rem 0.5rem;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  color: #e0e0e0;
  font-size: 0.875rem;
  cursor: pointer;
}

.calendar-dropdown .weekday-header {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
  margin-bottom: 0.5rem;
}

.calendar-dropdown .weekday {
  text-align: center;
  font-size: 0.75rem;
  font-weight: 600;
  color: #999;
  padding: 0.25rem;
}

.calendar-dropdown .days-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
}

.calendar-dropdown .day-btn {
  width: 100%;
  aspect-ratio: 1;
  padding: 0;
  background: transparent;
  border: none;
  border-radius: 50%;
  color: #e0e0e0;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.calendar-dropdown .day-btn:hover:not(.is-disabled):not(.is-selected) {
  background: rgba(255, 255, 255, 0.1);
}

.calendar-dropdown .day-btn.is-other-month {
  color: #999;
  opacity: 0.5;
}

.calendar-dropdown .day-btn.is-today {
  border: 2px solid var(--color-primary, #3b88a5);
}

.calendar-dropdown .day-btn.is-selected {
  background: var(--color-primary, #3b88a5);
  color: white;
}

.calendar-dropdown .day-btn.is-disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.calendar-dropdown .calendar-footer {
  display: flex;
  justify-content: center;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.calendar-dropdown .today-btn {
  padding: 0.5rem 1rem;
  background: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: 12px;
  color: var(--color-primary, #3b88a5);
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.calendar-dropdown .today-btn:hover {
  background: var(--color-primary, #3b88a5);
  color: white;
}

/* Dropdown transition */
.dropdown-enter-active,
.dropdown-leave-active {
  transition: all 0.2s ease;
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>
